import Link from "@src/app/components/Link";
import type { BFFArticleBodyElement } from "@src/app/types/bff";
import { Fragment, type ReactNode } from "react";

type TagOperationType = {
  index: number;
  length: number;
  type: "start" | "end";
  tag: string;
  tagName: string;
  annotation: BFFArticleBodyElement["paragraph"]["annotations"][0];
};

const tagMap = {
  bold: "strong",
  underline: "u",
  italic: "em",
  subscript: "sub",
  superscript: "sup",
  strike: "s",
  external_link: "a",
  internal_link: "a",
};

export default function ArticleAnnotation({
  value = "",
  annotations = [],
  showLog = false,
}: Pick<BFFArticleBodyElement["paragraph"], "annotations" | "value"> & {
  showLog?: boolean;
}) {
  if (!value) return null;

  const tagOperations: TagOperationType[] = annotations.reduce(
    (acc: TagOperationType[], item) => {
      const annotation = item;
      acc.push({
        index: item.index,
        length: item.length,
        type: "start",
        tag: Object.keys(tagMap).includes(item.name)
          ? `<${tagMap[item.name as keyof typeof tagMap]}>`
          : "",
        tagName: Object.keys(tagMap).includes(item.name)
          ? tagMap[item.name as keyof typeof tagMap]
          : "",
        annotation,
      });
      acc.push({
        index: item.index + item.length,
        length: item.length,
        type: "end",
        tag: Object.keys(tagMap).includes(item.name)
          ? `</${tagMap[item.name as keyof typeof tagMap]}>`
          : "",
        tagName: Object.keys(tagMap).includes(item.name)
          ? tagMap[item.name as keyof typeof tagMap]
          : "",
        annotation,
      });
      return acc;
    },
    [],
  );

  // Sort tag operations
  tagOperations.sort((a, b) => {
    if (a.index === b.index) {
      // End tags should come before start tags if at the same index
      if (a.type == b.type) {
        if (a.length == b.length) {
          return a.type == "start"
            ? a.tagName.localeCompare(b.tagName)
            : b.tagName.localeCompare(a.tagName);
        }
        return a.type == "start" ? b.length - a.length : a.length - b.length;
      }
      return a.type === "end" ? -1 : 1;
    }
    return a.index - b.index;
  });

  showLog && console.log(tagOperations, "tagOperations result", annotations);

  const render = (
    renderValue: string,
    operations: TagOperationType[],
  ): ReactNode => {
    if (!renderValue) return null;
    if (!operations.length) {
      if (renderValue.includes("\n"))
        return <span className=" whitespace-pre-wrap">{renderValue}</span>;
      return renderValue;
    }
    if (operations.length == 1) return null;

    const operation = operations[0];

    if (operation.type != "start") {
      return null;
    }

    const operationEndIndex = operations.findIndex(
      (i) =>
        i.type == "end" &&
        i.tagName == operation.tagName &&
        i.index == operation.index + operation.length,
    );

    const satisfyFinalCondition =
      operation.index == 0 &&
      operationEndIndex == operations.length - 1 &&
      renderValue.length <= operation.length; // 1350222 infinite loop

    if (satisfyFinalCondition) {
      const _renderValue = render(renderValue, operations.slice(1, -1));

      // external link or internal link
      if (operation.tagName == "a") {
        const newWindow = operation.annotation.newWindow;
        const noFollow = operation.annotation.noFollow;
        const name = operation.annotation.name;
        return (
          <Link
            to={
              name == "internal_link"
                ? `${operation.annotation.url}?ref=inline-article`
                : operation.annotation.url || ""
            }
            // external link
            {...(name == "external_link"
              ? {
                  target: newWindow ? "_blank" : "_self",
                  rel: noFollow ? "nofollow" : undefined,
                }
              : {})}
          >
            {_renderValue}
          </Link>
        );
      } else {
        const Tag = operation.tagName as unknown as
          | keyof HTMLElementTagNameMap
          | undefined;
        return Tag ? <Tag>{_renderValue}</Tag> : <span>{_renderValue}</span>;
      }
    }

    const startIndex = operation.index;
    const endIndex = operation.index + operation.length - 1;

    const acc: { renderValue: string; operations: TagOperationType[] }[] = [
      { renderValue: renderValue.slice(0, startIndex), operations: [] },
      {
        renderValue: renderValue.slice(startIndex, endIndex + 1),
        operations: operations
          .slice(0, operationEndIndex + 1)
          .map((i) => ({ ...i, index: i.index - startIndex })),
      },
      {
        renderValue: renderValue.slice(endIndex + 1),
        operations: operations
          .slice(operationEndIndex + 1)
          .map((i) => ({ ...i, index: i.index - (endIndex + 1) })),
      },
    ];

    showLog && console.log(acc, "acc result");

    // 1353074 infinite loop
    return acc
      .map((i, index) => {
        const renderedRes = render(i.renderValue, i.operations);
        return renderedRes ? (
          <Fragment key={index}>{renderedRes}</Fragment>
        ) : null;
      })
      .filter(Boolean);
  };

  const result = render(value, tagOperations);
  showLog && console.log(result, "result111111");

  return <span>{result}</span>;
}
